"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { stegaClean } from "next-sanity";

import type {
  CommunityAvatar,
  IDiscordMessage,
} from "./types";

const HAIRLINE = "rgba(51,51,51,0.5)";
const TYPING_MS = 2000;
const AFTER_MESSAGE_MS = 1400;
const HOLD_MS = 5000;
const RESET_MS = 1000;
const AVATAR_BACKGROUNDS = ["#20272b", "#27231f", "#22252c", "#282129", "#1f2825", "#252329"];

type Variant = "desktop" | "mobile";

interface ChatMessagesProps {
  variant: Variant;
  messages?: IDiscordMessage[];
  liveMessages?: IDiscordMessage[];
  yesterdayLabel?: string;
  todayLabel?: string;
  typingSuffix?: string;
}

type Step = { kind: "typing" | "message"; message: IDiscordMessage };
const spring = { type: "spring", stiffness: 150, damping: 26, mass: 0.9 } as const;
const EASE = [0.25, 0.1, 0.25, 1] as const;

function getBlurDataURL(image?: CommunityAvatar) {
  if (!image) return undefined;
  return "lqip" in image ? image.lqip : image.blurDataURL;
}

function Avatar({ message, variant }: { message: IDiscordMessage; variant: Variant }) {
  const size = variant === "desktop" ? "h-[5rem] w-[5rem]" : "h-[4.25rem] w-[4.25rem]";
  const initial = message.avatarInitial?.trim() || message.author?.trim().charAt(0).toUpperCase() || "?";
  const colorIndex = (message.author ?? initial)
    .split("")
    .reduce((total, character) => total + character.charCodeAt(0), 0);
  const blurDataURL = getBlurDataURL(message.avatar);

  return (
    <div
      className={`relative flex ${size} shrink-0 items-center justify-center overflow-clip rounded-full`}
      style={{
        backgroundColor: message.avatarColor
          ? stegaClean(message.avatarColor)
          : AVATAR_BACKGROUNDS[colorIndex % AVATAR_BACKGROUNDS.length],
      }}>
      {message.avatar?.src ? (
        <Image
          src={message.avatar.src}
          alt=""
          fill
          sizes={variant === "desktop" ? "40px" : "34px"}
          placeholder={blurDataURL ? "blur" : undefined}
          blurDataURL={blurDataURL}
          className="object-cover grayscale"
        />
      ) : (
        <span
          className={`font-geist leading-[1.5] font-semibold text-white/[0.65] ${
            variant === "desktop" ? "text-[1.875rem]" : "text-[1.625rem]"
          }`}>
          {initial}
        </span>
      )}
    </div>
  );
}

function Separator({ label, variant }: { label: string; variant: Variant }) {
  const isDesktop = variant === "desktop";
  return (
    <div className={`flex shrink-0 items-center ${isDesktop ? "w-[84rem] gap-[1.625rem] p-[1.25rem]" : "w-[45.75rem] gap-[1.25rem] p-[1rem]"}`}>
      <div
        className="h-px flex-1"
        style={{ backgroundColor: HAIRLINE }}
      />
      <span
        className={`font-geist leading-[1.5] font-semibold whitespace-nowrap text-white/30 ${
          isDesktop ? "text-[1.375rem]" : "text-[1.25rem]"
        }`}>
        {label}
      </span>
      <div
        className="h-px flex-1"
        style={{ backgroundColor: HAIRLINE }}
      />
    </div>
  );
}

function MessageRow({ message, variant, imageFirst }: { message: IDiscordMessage; variant: Variant; imageFirst: boolean }) {
  const isDesktop = variant === "desktop";
  const imageBlurDataURL = getBlurDataURL(message.image);
  const chart = message.image && (
    <div
      className={`relative shrink-0 overflow-clip rounded-[0.75rem] border ${
        isDesktop ? "h-[23.25rem] w-[37.5rem]" : "h-[19.5rem] w-[31.5rem]"
      }`}
      style={{ borderColor: HAIRLINE }}>
      <Image
        src={message.image.src}
        alt=""
        fill
        sizes={isDesktop ? "37.5rem" : "31.5rem"}
        placeholder={imageBlurDataURL ? "blur" : undefined}
        blurDataURL={imageBlurDataURL}
        className="object-cover"
      />
    </div>
  );

  return (
    <div className={`flex items-start rounded-[0.75rem] ${isDesktop ? "gap-[1.75rem] px-[1.25rem] py-[0.875rem]" : "gap-[1.375rem] px-[1rem] py-[0.75rem]"}`}>
      <Avatar
        message={message}
        variant={variant}
      />
      <div className={`flex flex-col items-start ${isDesktop ? "gap-[0.375rem]" : "gap-[0.25rem]"}`}>
        {imageFirst && chart}
        <div className={`flex items-baseline leading-[1.5] whitespace-nowrap ${isDesktop ? "gap-[1.25rem]" : "gap-[1rem]"}`}>
          <span className={`font-geist font-medium text-white ${isDesktop ? "text-[2rem]" : "text-[1.75rem]"}`}>{message.author}</span>
          <span className={`font-geist text-white/30 ${isDesktop ? "text-[1.625rem]" : "text-[1.375rem]"}`}>{message.time}</span>
        </div>
        <p className={`m-0 font-geist leading-[1.5] text-white/[0.72] ${isDesktop ? "w-[70rem] text-[2rem]" : "w-[36.25rem] text-[1.75rem]"}`}>
          {message.text}
        </p>
        {!imageFirst && chart}
        {message.tag && (
          <div
            className={`flex shrink-0 items-start rounded-[0.75rem] border-[0.175rem] ${
              isDesktop ? "px-[1.625rem] py-[1rem]" : "px-[1.25rem] py-[0.75rem]"
            }`}
            style={{ borderColor: HAIRLINE }}>
            <span className={`font-geist leading-[1.5] whitespace-pre text-white/70 ${isDesktop ? "text-[1.625rem]" : "text-[1.375rem]"}`}>
              {message.tag}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TypingRow({
  message,
  variant,
  typingSuffix,
}: {
  message: IDiscordMessage;
  variant: Variant;
  typingSuffix?: string;
}) {
  const isDesktop = variant === "desktop";
  return (
    <div className={`flex items-center ${isDesktop ? "gap-[1.75rem] px-[1.25rem] py-[0.875rem]" : "gap-[1.375rem] px-[1rem] py-[0.75rem]"}`}>
      <Avatar
        message={message}
        variant={variant}
      />
      <div className={`flex items-center ${isDesktop ? "gap-[0.875rem]" : "gap-[0.75rem]"}`}>
        <span className={`font-geist leading-[1.5] whitespace-nowrap text-[rgba(255,255,255,0.38)] ${isDesktop ? "text-[1.875rem]" : "text-[1.625rem]"}`}>
          {message.author} {typingSuffix}
        </span>
        <div className={`flex ${isDesktop ? "gap-[0.625rem]" : "gap-[0.5rem]"}`}>
          {[0, 1, 2].map((dot) => (
            <motion.span
              key={dot}
              className={`rounded-full bg-white/60 ${isDesktop ? "h-[0.75rem] w-[0.75rem]" : "h-[0.625rem] w-[0.625rem]"}`}
              animate={{ opacity: [0.25, 1, 0.25] }}
              transition={{ duration: 1.1, repeat: Infinity, delay: dot * 0.22, ease: "easeInOut" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ChatMessages({
  variant,
  messages,
  liveMessages,
  yesterdayLabel,
  todayLabel,
  typingSuffix,
}: ChatMessagesProps) {
  const reduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { amount: 0.3 });

  const steps: Step[] = (liveMessages ?? []).flatMap((message) => {
    const own: Step[] = [];
    if (message.showTyping) own.push({ kind: "typing", message });
    own.push({ kind: "message", message });
    return own;
  });

  const [visibleSteps, setVisibleSteps] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (reduceMotion || steps.length === 0 || !inView) return;

    if (visibleSteps >= steps.length + 1) return;

    let delay: number;
    if (visibleSteps === 0) {
      delay = AFTER_MESSAGE_MS;
    } else {
      const current = steps[visibleSteps - 1];
      delay = current.kind === "typing" ? TYPING_MS : AFTER_MESSAGE_MS;
    }

    const timeout = setTimeout(() => {
      if (visibleSteps < steps.length) {
        setVisibleSteps(visibleSteps + 1);
      } else {
        setTimeout(() => {
          setVisibleSteps(0);
          setTimeout(() => setCycle((c) => c + 1), RESET_MS);
        }, HOLD_MS - AFTER_MESSAGE_MS);
        setVisibleSteps(steps.length + 1);
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [visibleSteps, inView, reduceMotion, steps.length, cycle]);

  const shown = Math.min(visibleSteps, steps.length);
  const liveRows = steps.slice(0, shown).filter((step, i) => step.kind === "message" || i === shown - 1);

  const isDesktop = variant === "desktop";

  return (
    <div
      ref={containerRef}
      className={`absolute flex flex-col items-start justify-end gap-[0.25rem] overflow-clip ${
        isDesktop ? "top-[9rem] left-[39rem] h-[66.875rem] w-[89rem] p-[2.5rem]" : "top-[6.875rem] left-0 h-[50rem] w-full p-[1.5rem]"
      }`}>
      <Separator
        label={yesterdayLabel ?? ""}
        variant={variant}
      />
      {messages?.map((message, i) => (
        <div
          key={i}
          className="shrink-0">
          <MessageRow
            message={message}
            variant={variant}
            imageFirst={isDesktop && i === 0}
          />
        </div>
      ))}
      <Separator
        label={todayLabel ?? ""}
        variant={variant}
      />

      {reduceMotion ? (
        liveMessages?.map((message, i) => (
          <div
            key={i}
            className="shrink-0">
            <MessageRow
              message={message}
              variant={variant}
              imageFirst={false}
            />
          </div>
        ))
      ) : (
        <AnimatePresence mode="popLayout">
          {liveRows.map((step) => (
            <motion.div
              key={`${cycle}-${step.message.author}-${step.message.time}-${step.kind}`}
              layout
              className="shrink-0"
              initial={{ opacity: 0, y: 28, scale: 0.985, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{
                opacity: 0,
                y: -10,
                filter: "blur(4px)",
                transition: { duration: 0.55, ease: EASE },
              }}
              transition={{
                layout: spring,
                ...spring,
                opacity: { duration: 0.55, ease: EASE },
                filter: { duration: 0.5, ease: EASE },
              }}>
              {step.kind === "typing" ? (
                <TypingRow
                  message={step.message}
                  variant={variant}
                  typingSuffix={typingSuffix}
                />
              ) : (
                <MessageRow
                  message={step.message}
                  variant={variant}
                  imageFirst={false}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      )}
    </div>
  );
}
